<template>
  <div class="game-slider">
    <!--    <h2 class="title">{{ title }}</h2>-->

    <div
      class="carousel-wrapper"
      ref="carouselWrapper"
      @wheel.passive="handleWheel"
    >
      <Carousel
        v-bind="config"
        ref="carousel"
        v-model="currentSlide"
        @update:modelValue="handleSlideUpdate"
        class="game-carousel"
      >
        <Slide v-for="(slide, index) in slides" :key="index">
          <div
            class="carousel__item"
            @click="(e) => handleSlideClick(slide, index, e)"
            @mousedown="handleMouseDown"
            @touchstart="(e) => handleMouseDown(e.touches[0])"
            :class="{
              'is-dragging': isDragging || isSliding,
              'is-center': index === currentSlide,
              'is-side': index !== currentSlide,
              'is-placeholder': isPlaceholderSlide(slide),
            }"
          >
            <img
              v-if="!isPlaceholderSlide(slide)"
              :src="slide.image"
              :alt="slide.name"
              draggable="false"
              decoding="async"
              :class="{ 'is-loaded': loadedImages[slide.image] }"
              @load="() => markImageLoaded(slide.image)"
              @error="() => markImageLoaded(slide.image)"
            />
            <div v-else class="carousel__placeholder" aria-hidden="true"></div>
          </div>
        </Slide>
        <div></div>
        <template #addons>
          <!--          <Navigation />-->
          <Pagination />
        </template>
      </Carousel>
    </div>
    <div>
      <button
        @click.stop.prevent="handlePrevClick"
        class="nav-button prev"
        :disabled="isNavigationBlocked()"
        :class="{ 'navigation-locked': isNavigationBlocked() }"
      >
        <img :src="props.leftArrowSrc" alt="go to previous slide" />
      </button>
      <button
        @click.stop.prevent="handleNextClick"
        class="nav-button next"
        :disabled="isNavigationBlocked()"
        :class="{ 'navigation-locked': isNavigationBlocked() }"
      >
        <img :src="props.rightArrowSrc" alt="go to next slide" />
      </button>
    </div>
  </div>
</template>

<script setup>
import 'vue3-carousel/dist/carousel.css';
import { Carousel, Slide, Pagination } from 'vue3-carousel';
import { ref, onMounted, onUnmounted, watch, nextTick } from 'vue';
import { useSwipe } from '@vueuse/core';
import { navigateToGame } from '@/router';

const props = defineProps({
  title: {
    type: String,
    default: 'Pick a game',
  },
  slides: {
    type: Array,
    required: true,
  },
  initialSlide: {
    type: Number,
    default: 0,
  },
  leftArrowSrc: {
    type: String,
    required: true,
  },
  rightArrowSrc: {
    type: String,
    required: true,
  },
});

const next = () => {
  if (isNavigationBlocked()) {
    console.log('[Slider] Next blocked - navigation locked');
    return;
  }
  console.log('[Slider] Next triggered - using DIRECT VALUE update');

  // Set locks immediately
  navigationLock.value = true;
  isSliding.value = true;

  // Instead of using slideTo, update currentSlide value directly
  const nextSlide = getAdjacentPlayableRenderedIndex(
    currentSlide.value,
    'next'
  );

  console.log(
    '[Slider] Direct value update from',
    currentSlide.value,
    'to',
    nextSlide
  );

  // Update the value directly - let Vue's reactivity handle it
  currentSlide.value = nextSlide;

  // No animation, so minimal timeout
  setTimeout(() => {
    console.log('[Slider] Direct next timeout complete');
    isSliding.value = false;
    navigationLock.value = false;
  }, 50); // Minimal timeout since no animation
};

const prev = () => {
  if (isNavigationBlocked()) {
    console.log('[Slider] Prev blocked - navigation locked');
    return;
  }
  console.log('[Slider] Prev triggered - using DIRECT VALUE update');

  // Set locks immediately
  navigationLock.value = true;
  isSliding.value = true;

  // Instead of using slideTo, update currentSlide value directly
  const prevSlide = getAdjacentPlayableRenderedIndex(
    currentSlide.value,
    'prev'
  );

  console.log(
    '[Slider] Direct value update from',
    currentSlide.value,
    'to',
    prevSlide
  );

  // Update the value directly - let Vue's reactivity handle it
  currentSlide.value = prevSlide;

  // No animation, so minimal timeout
  setTimeout(() => {
    console.log('[Slider] Direct prev timeout complete');
    isSliding.value = false;
    navigationLock.value = false;
  }, 50); // Minimal timeout since no animation
};

// Enhanced click handlers with event prevention
const handleNextClick = (event) => {
  event.preventDefault();
  event.stopPropagation();
  console.log('[Slider] Button next clicked - calling next()');
  next();
};

const handlePrevClick = (event) => {
  event.preventDefault();
  event.stopPropagation();
  console.log('[Slider] Button prev clicked - calling prev()');
  prev();
};

const emit = defineEmits(['slide-change', 'game-selected']);
const currentSlide = ref(props.initialSlide);
const loadedImages = ref({});

const isDragging = ref(false);
const isSliding = ref(false);
const navigationLock = ref(false); // Additional lock to prevent any navigation

const carousel = ref(null);
const carouselWrapper = ref(null);
const isPlaceholderSlide = (slide) => Boolean(slide?.isPlaceholder);

const getSlideRealIndex = (slide, fallbackIndex) => {
  return typeof slide?.realIndex === 'number' ? slide.realIndex : fallbackIndex;
};

const getFirstPlayableRenderedIndex = () => {
  const slides = props.slides ?? [];
  const firstPlayableIndex = slides.findIndex(
    (slide) => !isPlaceholderSlide(slide)
  );
  return firstPlayableIndex >= 0 ? firstPlayableIndex : 0;
};

const normalizePlayableRenderedIndex = (renderedIndex) => {
  const slides = props.slides ?? [];
  const totalSlides = slides.length;
  if (!totalSlides) return 0;

  const wrappedIndex =
    ((renderedIndex % totalSlides) + totalSlides) % totalSlides;
  if (!isPlaceholderSlide(slides[wrappedIndex])) return wrappedIndex;

  for (let offset = 1; offset < totalSlides; offset += 1) {
    const left = (wrappedIndex - offset + totalSlides) % totalSlides;
    if (!isPlaceholderSlide(slides[left])) return left;

    const right = (wrappedIndex + offset) % totalSlides;
    if (!isPlaceholderSlide(slides[right])) return right;
  }

  return wrappedIndex;
};

const resolveRealIndexFromRenderedIndex = (renderedIndex) => {
  const slides = props.slides ?? [];
  if (!slides.length) return 0;
  const playableIndex = normalizePlayableRenderedIndex(renderedIndex);
  return getSlideRealIndex(slides[playableIndex], playableIndex);
};

const resolveRenderedIndexFromRealIndex = (realIndex) => {
  const slides = props.slides ?? [];
  if (!slides.length) return 0;

  const mappedIndex = slides.findIndex(
    (slide, index) =>
      !isPlaceholderSlide(slide) &&
      getSlideRealIndex(slide, index) === realIndex
  );

  if (mappedIndex >= 0) return mappedIndex;
  if (realIndex >= 0 && realIndex < slides.length) {
    return normalizePlayableRenderedIndex(realIndex);
  }

  return getFirstPlayableRenderedIndex();
};

const getAdjacentPlayableRenderedIndex = (startIndex, direction) => {
  const slides = props.slides ?? [];
  const totalSlides = slides.length;
  if (!totalSlides) return 0;

  const normalizedStartIndex = normalizePlayableRenderedIndex(startIndex);
  let cursor = normalizedStartIndex;

  for (let attempts = 0; attempts < totalSlides; attempts += 1) {
    cursor =
      direction === 'next'
        ? (cursor + 1) % totalSlides
        : (cursor - 1 + totalSlides) % totalSlides;

    if (!isPlaceholderSlide(slides[cursor])) {
      return cursor;
    }
  }

  return normalizedStartIndex;
};

const markImageLoaded = (src) => {
  if (!src) return;
  loadedImages.value[src] = true;
};

// Helper function to check if any navigation should be blocked
const isNavigationBlocked = () => {
  return isSliding.value || isDragging.value || navigationLock.value;
};

// Set up swipe gestures for touch navigation
const { direction } = useSwipe(carouselWrapper, {
  threshold: 50, // Minimum distance for a swipe to be recognized
  onSwipeEnd: () => {
    if (isNavigationBlocked()) {
      console.log('[Slider] Swipe blocked - navigation locked');
      return;
    }

    // Check swipe direction
    if (direction.value === 'left') {
      console.log('[Slider] Swipe left detected - going to next slide');
      next();
    } else if (direction.value === 'right') {
      console.log('[Slider] Swipe right detected - going to previous slide');
      prev();
    }
  },
});

// Watch for slide changes and emit the event
watch(currentSlide, (newValue) => {
  emit('slide-change', resolveRealIndexFromRenderedIndex(newValue));
});

// Watch for initialSlide prop changes and update currentSlide
watch(
  () => props.initialSlide,
  (newValue, oldValue) => {
    if (isNavigationBlocked() || newValue === oldValue) {
      console.log('[Slider] initialSlide watcher blocked:', {
        isSliding: isSliding.value,
        isDragging: isDragging.value,
        navigationLock: navigationLock.value,
        unchanged: newValue === oldValue,
      });
      return;
    }
    console.log(
      '[Slider] initialSlide prop changed from',
      oldValue,
      'to:',
      newValue
    );

    // Map from real slide index to rendered index (rendered list may include placeholders)
    const mappedRenderedIndex = resolveRenderedIndexFromRealIndex(newValue);
    if (mappedRenderedIndex !== currentSlide.value) {
      console.log(
        '[Slider] Direct update currentSlide value to mapped index:',
        mappedRenderedIndex
      );
      currentSlide.value = mappedRenderedIndex;
    }
  },
  { immediate: false } // Don't run immediately to avoid conflicts on mount
);

watch(
  () => props.slides,
  (newSlides) => {
    if (!newSlides?.length) {
      currentSlide.value = 0;
      return;
    }

    const currentRealIndex = resolveRealIndexFromRenderedIndex(
      currentSlide.value
    );
    const mappedRenderedIndex =
      resolveRenderedIndexFromRealIndex(currentRealIndex);

    if (mappedRenderedIndex !== currentSlide.value) {
      currentSlide.value = mappedRenderedIndex;
    }
  },
  { deep: false }
);

const config = {
  itemsToShow: 3,
  wrapAround: true, // Re-enable wrapAround
  snapAlign: 'center',
  mouseDrag: false, // Disable dragging to prevent internal transitions
  touchDrag: false, // Disable touch dragging to prevent internal transitions
  itemsToScroll: 1,
  transition: 0, // No animation - instant snap
  slideEffect: 'slide', // Explicitly set slide effect
  // Additional config to completely disable transitions
  autoplay: 0, // Disable autoplay
  pauseAutoplayOnHover: false,
  dir: 'ltr',
  // No animation across all breakpoints
  breakpoints: {
    // Remove the 700px breakpoint to keep 3 items showing on tablets in portrait
    1024: {
      itemsToShow: 3,
      transition: 0,
      mouseDrag: false,
      touchDrag: false,
    },
  },
};

const clickStartTime = ref(0);
const clickPosition = ref({ x: 0, y: 0 });

const handleMouseDown = (event) => {
  clickStartTime.value = Date.now();
  clickPosition.value = {
    x: event.clientX,
    y: event.clientY,
  };
};

const handleSlideClick = (slide, index, event) => {
  if (isPlaceholderSlide(slide)) {
    console.log('[Slider] Placeholder slide click ignored');
    return;
  }
  const clickDuration = Date.now() - clickStartTime.value;
  const moveDistance = Math.sqrt(
    Math.pow(event.clientX - clickPosition.value.x, 2) +
      Math.pow(event.clientY - clickPosition.value.y, 2)
  );

  // Only allow clicks on the center slide
  if (index !== currentSlide.value) {
    console.log(
      '[Slider] Click ignored - slide',
      index,
      'is not center slide',
      currentSlide.value
    );
    return;
  }

  console.log('[Slider] Center slide clicked:', slide.name, 'index:', index);

  if (clickDuration < 200 && moveDistance < 5 && slide.short_name) {
    navigateToGame(slide.short_name).catch((error) => {
      console.error('Navigation error:', error);
    });
  }
};

const handleSlideUpdate = (newValue) => {
  const playableValue = normalizePlayableRenderedIndex(newValue);
  console.log(
    '[Slider] Carousel updated to slide:',
    playableValue,
    'from:',
    currentSlide.value
  );

  // Just track the slide change
  if (playableValue !== currentSlide.value) {
    currentSlide.value = playableValue;
  }
};

const handleWheel = (e) => {
  if (isNavigationBlocked()) {
    console.log('[Slider] Wheel blocked - navigation locked');
    return;
  }

  console.log('[Slider] Wheel event triggered - using CONTROLLED navigation');
  navigationLock.value = true;
  isSliding.value = true;

  nextTick(() => {
    if (e.deltaX > 0 || e.deltaY > 0) {
      next(); // Use our controlled next function
    } else {
      prev(); // Use our controlled prev function
    }
  });

  setTimeout(() => {
    console.log('[Slider] Wheel timeout - releasing locks');
    isSliding.value = false;
    navigationLock.value = false;
  }, 50);
};

const debounce = (fn, delay) => {
  let timeoutId;
  return (...args) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    timeoutId = setTimeout(() => {
      fn(...args);
    }, delay);
  };
};

const handleKeyPress = debounce((e) => {
  if (isNavigationBlocked()) {
    console.log('[Slider] Keyboard blocked - navigation locked');
    return;
  }

  if (e.key === 'ArrowRight') {
    console.log('[Slider] Keyboard right arrow - using CONTROLLED navigation');
    next(); // Use our controlled next function
  } else if (e.key === 'ArrowLeft') {
    console.log('[Slider] Keyboard left arrow - using CONTROLLED navigation');
    prev(); // Use our controlled prev function
  }
}, 300);

onMounted(() => {
  window.addEventListener('keydown', handleKeyPress);

  // Set initial slide value
  currentSlide.value = resolveRenderedIndexFromRealIndex(props.initialSlide);

  // Emit initial slide position
  emit('slide-change', resolveRealIndexFromRenderedIndex(currentSlide.value));

  // Just ensure currentSlide matches initialSlide - no slideTo needed
  const mappedInitialIndex = resolveRenderedIndexFromRealIndex(
    props.initialSlide
  );
  if (mappedInitialIndex !== currentSlide.value) {
    currentSlide.value = mappedInitialIndex;
  }
});

onUnmounted(() => {
  window.removeEventListener('keydown', handleKeyPress);
});
</script>

<style lang="scss" scoped>
.game-slider {
  // width: 100%;
  // max-width: 100vw;
  width: 200vw;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 2rem 0;

  @media screen and (max-aspect-ratio: 9/5) {
    //transform: scale(0.9);
  }

  @media (max-width: 1024px) and (orientation: portrait) {
    padding: 0;
    margin-top: 15vh;
    /* transform: scale(1.1); */
  }
}

@media (prefers-reduced-motion: reduce) {
  .carousel__item img {
    transition: none;
  }
}

.title {
  font-size: 2rem;
  margin-bottom: 2rem;
  text-align: center;
}

.carousel-wrapper {
  width: 100%;
  position: relative;
  // overflow: hidden;
}

.game-carousel {
  .carousel__viewport {
    // overflow: visible;
  }

  .carousel__track {
    display: flex;
    align-items: center;
    gap: 2rem;
    // Force no transitions on the track itself
    transition: none !important;
    transform-style: preserve-3d;
  }

  .carousel__slide {
    opacity: 0.5;
    transform: scale(0.85);
    transition:
      opacity 0.3s ease,
      transform 0.3s ease; // Keep the fade transitions

    &--active {
      opacity: 1;
      transform: scale(1);
      z-index: 2;
    }
  }
}

// Override ONLY the carousel track movement transitions, keep slide fade effects
:deep(.carousel__track) {
  transition: none !important;
  animation: none !important;
  transform-origin: 0 0 !important;
}

// Keep slide transitions for fade effects, but disable during movement
:deep(.carousel__slide) {
  // Keep the normal transition for opacity/scale changes
  // Only disable during actual slide movements
  &.carousel__slide--moving {
    transition: none !important;
    animation: none !important;
  }
}

.carousel__pagination {
  position: absolute;
  bottom: -3vh;
}

.carousel__item {
  width: 100%;
  aspect-ratio: 800 / 493;
  cursor: pointer;
  padding: 0 1rem;
  transition: all 0.3s ease;

  &.is-center {
    cursor: pointer;
    transition: scale 0.2s ease; // Keep hover transition
    &:hover {
      scale: 1.03;
    }
  }

  &.is-side {
    cursor: default;
    opacity: 1;
    &:hover {
      scale: 1; // No hover effect for side slides
    }
  }

  &.is-dragging {
    cursor: grabbing;
    pointer-events: none;
  }

  &.is-placeholder {
    cursor: default;
    pointer-events: none;

    &:hover {
      scale: 1;
    }
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    border-radius: 8px;
    user-select: none;
    opacity: 0;
    transition: opacity 0.35s ease;
    &:hover {
      cursor: pointer;
    }
    &.is-loaded {
      opacity: 1;
    }
  }

  .carousel__placeholder {
    width: 100%;
    height: 100%;
    border-radius: 8px;
    background: rgba(128, 128, 128, 0.25);
    border: 1px solid rgba(128, 128, 128, 0.4);
  }
}

.description-container {
  width: 100%;
  max-width: 800px;
  margin: 2rem auto 0;
  padding: 0 1rem;
}

.description-box {
  background: white;
  border-radius: 8px;
  padding: 1rem 2rem;
  text-align: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  font-style: italic;

  &.no-description {
    color: #666;
  }
}

.nav-button {
  position: absolute;
  top: 40vh;
  //transform: translateY(-10vh);
  background-color: #fff;
  border: none;
  cursor: pointer;
  padding: 1rem;
  z-index: 3;
  display: flex;
  align-items: center;

  &:disabled,
  &.navigation-locked {
    opacity: 0.5;
    cursor: not-allowed;
    pointer-events: none;

    &:hover {
      transform: none !important;
    }
  }
  justify-content: center;

  &:hover {
    transform: none;
  }

  &.prev {
    left: 2vw;
    img {
      transform: rotate(180deg);
    }
  }

  &.next {
    right: 2vw;
  }

  img {
    cursor: pointer;
    width: 48px;
    height: 48px;
  }
}

@media (max-width: 768px) {
  .carousel__item {
    padding: 0 0.5rem;
  }

  .nav-button {
    img {
      width: 32px;
      height: 32px;
    }
  }
}
</style>
